<template>
  <div class="task-wrapper">
    
    <h2 class="task-title">{{ t("slotTask.title") }}</h2>

    <!-- No Tasks -->
    <div v-if="tasks.length === 0" class="no-task">
      {{ t("slotTask.noTask") }}
    </div>

    <!-- Task Grid -->
    <div class="task-grid">
      <div 
        v-for="task in paginatedTasks" 
        :key="task._id"
        class="task-card"
      >

        <!-- Game Info -->
        <div class="game-info">
          <img :src="task.gameId?.banner" class="game-banner" />

          <div>
            <h3 class="game-name">{{ task.gameId?.game_name }}</h3>
            <p class="provider">{{ task.gameId?.provider?.name }}</p>
          </div>
        </div>

        <!-- Dates -->
        <div class="date-box">
          <p>📅 {{ t("slotTask.start") }}: <strong>{{ formatDate(task.createdAt) }}</strong></p>
          <p>⏳ {{ t("slotTask.end") }}: <strong>{{ formatDate(task.endsAt) }}</strong></p>
        </div>

        <!-- Progress -->
        <div class="progress-box">

          <div class="numbers">
            <span class="label">{{ t("slotTask.progress") }}:</span>
            <span class="value">
              {{ formatCurrency(getUserProgress(task)._progress) }} /
              {{ formatCurrency(task.requiredBetFiat) }}
            </span>
          </div>

          <div class="progress-bar">
            <div 
              class="progress-fill" 
              :style="{ width: getUserProgress(task)._percent + '%' }">
            </div>
          </div>

          <p class="percent">{{ getUserProgress(task)._percent }}%</p>
        </div>

        <!-- Reward -->
        <div class="reward-box">
          <span>🏆 {{ t("slotTask.reward") }}:</span>
          <strong>{{ formatCurrency(task.rewardFiat) }}</strong>
        </div>

        <!-- Buttons -->
        <div class="actions">
          <button
            v-if="!getUserProgress(task)._completed"
            disabled
            class="btn disabled-btn"
          >
            {{ t("slotTask.inProgress") }}
          </button>

          <button
            v-else-if="!getUserProgress(task)._rewarded"
            class="btn reward-btn"
            @click="claimReward(task._id)"
          >
            🎁 {{ t("slotTask.claimReward") }}
          </button>

          <button
            v-else
            class="btn rewarded-btn"
            disabled
          >
            ✔ {{ t("slotTask.rewardTaken") }}
          </button>
        </div>

      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button @click="prevPage" :disabled="page === 1">‹</button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button @click="nextPage" :disabled="page === totalPages">›</button>
    </div>

    <!-- Popup -->
    <div v-if="showPopup" class="popup">
      🎉 {{ popupMessage }}
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const tasks = ref([]);
const showPopup = ref(false);
const popupMessage = ref("");

const page = ref(1);
const perPage = 4;

const formatCurrency = (v) => Number(v || 0).toFixed(2);
const formatDate = (d) => new Date(d).toLocaleDateString();

onMounted(async () => {
  const res = await axios.get("/slot-task/all");

  if (res.data.success) {
    tasks.value = res.data.tasks.map(t => ({
      ...t,
      requiredBetFiat: t.requiredBetUSD, // front-end çeviri istersen USD→FIAT yapabiliriz
      rewardFiat: t.rewardUSD
    }));
  }
});

const getUserProgress = (task) => {
  const userEntry = task.users?.find(
    u => u.userId === task.currentUserId || u.userId?._id === task.currentUserId
  );

  const progress = userEntry?.progressFiat || 0;
  const percent = Math.min(100, Math.floor((progress / task.requiredBetFiat) * 100));

  return {
    _progress: progress,
    _percent: percent,
    _completed: userEntry?.completed || false,
    _rewarded: userEntry?.rewarded || false
  };
};

/* Pagination */
const totalPages = computed(() =>
  Math.ceil(tasks.value.length / perPage)
);

const paginatedTasks = computed(() =>
  tasks.value.slice((page.value - 1) * perPage, page.value * perPage)
);

const nextPage = () => { if (page.value < totalPages.value) page.value++; };
const prevPage = () => { if (page.value > 1) page.value--; };

/* Claim Reward */
const claimReward = async (taskId) => {
  const res = await axios.post("/slot-task/claim");

  if (res.data.success) {
    popupMessage.value = `${formatCurrency(res.data.rewardFiat)} ${t("slotTask.rewardAdded")}`;
    showPopup.value = true;

    setTimeout(() => (showPopup.value = false), 2500);
  }
};
</script>

<style scoped>
/* WRAPPER */
.task-wrapper { max-width: 900px; margin: auto; padding: 20px; color: #fff; }
.task-title { text-align: center; font-size: 28px; margin-bottom: 20px; }

/* GRID */
.task-grid { 
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 18px;
}

/* CARD */
.task-card {
  background: var(--background2);
  border-radius: 18px;
  padding: 20px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 0 25px rgba(0,0,0,0.45);
}

/* GAME INFO */
.game-info {
  display: flex; align-items: center; gap: 14px;
}
.game-banner { width: 90px; height: 65px; border-radius: 12px; object-fit: cover; }
.game-name { font-size: 17px; font-weight: 700; }
.provider { color:#aaa; font-size: 14px; }

/* DATE BOX */
.date-box { margin-top: 10px; font-size: 13px; color: #bbb; }

/* PROGRESS */
.progress-box { margin-top: 16px; }
.numbers { display: flex; justify-content: space-between; }
.progress-bar {
  width:100%; height:14px; background:rgba(255,255,255,0.08);
  border-radius:10px; overflow:hidden; margin-top:5px;
}
.progress-fill {
  height:100%;
  background: linear-gradient(90deg, #4cffad, #05c77e);
  transition: width .3s;
}
.percent { text-align:right; color:#4cffad; font-size:13px; margin-top:3px; }

/* REWARD */
.reward-box {
  margin-top: 16px;
  background:rgba(255,255,255,0.06);
  border-radius:10px;
  padding:10px;
  display:flex;
  justify-content:space-between;
  font-size:16px;
}

/* BUTTONS */
.actions { margin-top: 15px; text-align:center; }
.btn { width:100%; padding:12px; border-radius:10px; font-size:15px; border:none; cursor:pointer; }
.reward-btn { background:#4cffad; color:#000; font-weight:700; }
.disabled-btn { background:#444; color:#aaa; }
.rewarded-btn { background:#333; color:#00ffae; }

/* POPUP */
.popup {
  background: rgba(0,255,136,0.25);
  padding:14px; border-radius:12px; margin-top:15px; text-align:center;
  font-weight:700; font-size:18px; color:#00ffae;
}

/* PAGINATION */
.pagination { margin-top:25px; text-align:center; font-size:18px; display:flex; justify-content:center; gap:20px; }
.pagination button { padding:8px 14px; border:none; border-radius:8px; background:#333; color:#fff; cursor:pointer; }
.pagination button:disabled { opacity:0.4; cursor:not-allowed; }
</style>
